import type { ValueOf } from './type';

// システムの一覧
export namespace Definition {
  export const System = {
    Auth: 'auth',
    Base: 'base',
    Lp: 'lp',
    ToolSetting: 'tool-setting',
    Troppus: 'troppus',
    Tabula: 'tabula',
    Knowledge: 'knowledge',
    Desk: 'desk',
    Speak: 'speak',
    Debeso: 'debeso',
    Debrief: 'debrief',
    Intelligence: 'intelligence',
    Voc: 'voc',
    QantDataHub: 'qant-data-hub',
    Issues: 'issues',
    Boilerplate: 'boilerplate',
    Neuro: 'neuro',
    Coach: 'coach',
    PubApi: 'pubapi',
    SmartFaq: 'smart-faq',
  } as const;
  export type System = ValueOf<typeof System>;

  export const Env = {
    Dev: 'dev', // --- ローカル環境
    Test: 'test', // - テスト用環境
    Eva: 'eva', // --- 検証環境 (リモート)
    Prod: 'prod', // - 本番環境
  } as const;
  export type Env = ValueOf<typeof Env>;

  export const GcloudProjectName = {
    Base: 'systems',
    Knowledge: System.Knowledge,
    Speak: System.Speak,
    Debeso: System.Debeso,
    Debrief: System.Debrief,
    Troppus: System.Troppus,
    Tabula: System.Tabula,
    Intelligence: System.Intelligence,
    Voc: System.Voc,
    QantDataHub: System.QantDataHub,
    Boilerplate: System.Boilerplate,
    Neuro: System.Neuro,
    Coach: System.Coach,
    PubApi: System.PubApi,
    SmartFaq: System.SmartFaq,
    // PCI DSS の CDE 専用プロジェクト。アプリではなく環境なので System には無い (apps/cde、docs/pcidss/adr/0004)。
    Cde: 'cde',
  } as const;
  export type GcloudProjectName = ValueOf<typeof GcloudProjectName>;

  export const Gcloud = {
    [Env.Dev]: { ProjectId: 'local-rt-systems' },
    [Env.Test]: { ProjectId: 'test-rt-systems' },
    [Env.Eva]: {
      ProjectId: 'evaluation-rt-systems',
      ProjectNumber: '282057777327',
      Pii: {
        ProjectId: 'evaluation-rt-systems-pii',
        ProjectNumber: '284891476675',
      },
      PlaidSystems: {
        ProjectId: 'evaluation-karte-io-systems',
        ProjectNumber: '405499578782',
      },
      GcpProject: {
        [GcloudProjectName.Base]: {
          ProjectId: 'evaluation-rt-systems',
          ProjectNumber: '282057777327',
        },
        [GcloudProjectName.Knowledge]: {
          ProjectId: 'evaluation-rt-knowledge',
          ProjectNumber: '164393146105',
        },
        [GcloudProjectName.Speak]: {
          ProjectId: 'evaluation-rt-speak',
          ProjectNumber: '97663201641',
        },
        [GcloudProjectName.Debeso]: {
          ProjectId: 'evaluation-rt-debeso',
          ProjectNumber: '277506514704',
        },
        [GcloudProjectName.Debrief]: {
          ProjectId: 'evaluation-rt-debrief',
          ProjectNumber: '586560341623',
        },
        [GcloudProjectName.Troppus]: {
          ProjectId: 'evaluation-rt-troppus',
          ProjectNumber: '941568787903',
        },
        [GcloudProjectName.Tabula]: {
          ProjectId: 'evaluation-rt-tabula',
          ProjectNumber: '506196712494',
        },
        [GcloudProjectName.Intelligence]: {
          ProjectId: 'evaluation-rt-intelligence',
          ProjectNumber: '317386895411',
        },
        [GcloudProjectName.Voc]: {
          ProjectId: 'evaluation-rt-voc',
          ProjectNumber: '601988953028',
        },
        [GcloudProjectName.QantDataHub]: {
          ProjectId: 'eva-qant-data-hub',
          ProjectNumber: '379186925220',
        },
        [GcloudProjectName.Boilerplate]: {
          ProjectId: 'evaluation-rt-boilerplate',
          ProjectNumber: '809633924503',
        },
        [GcloudProjectName.Neuro]: {
          ProjectId: 'evaluation-rt-neuro',
          ProjectNumber: '724675282975',
        },
        [GcloudProjectName.Coach]: {
          ProjectId: 'evaluation-rt-coach',
          ProjectNumber: '977013186091',
        },
        [GcloudProjectName.PubApi]: {
          ProjectId: 'evaluation-rt-pubapi',
          ProjectNumber: '384087460311',
        },
        [GcloudProjectName.SmartFaq]: {
          ProjectId: 'evaluation-rt-smart-faq',
          ProjectNumber: '559294432330',
        },
        [GcloudProjectName.Cde]: {
          ProjectId: 'evaluation-rt-cde',
          ProjectNumber: '900150948616',
        },
        Pii: {
          [GcloudProjectName.Base]: {
            ProjectId: 'evaluation-rt-systems-pii',
            ProjectNumber: '284891476675',
          },
          [GcloudProjectName.Troppus]: {
            ProjectId: 'evaluation-rt-troppus-pii',
            ProjectNumber: '579050452461',
          },
          [GcloudProjectName.Neuro]: {
            ProjectId: 'evaluation-rt-neuro-pii',
            ProjectNumber: '608617789051',
          },
          [GcloudProjectName.Speak]: {
            ProjectId: 'evaluation-rt-speak-pii',
            ProjectNumber: '833487490153',
          },
        },
      },
    },
    [Env.Prod]: {
      ProjectId: 'production-rt-systems',
      ProjectNumber: '402791595004',
      Pii: {
        ProjectId: 'production-rt-systems-pii',
        ProjectNumber: '768973697261',
      },
      PlaidSystems: {
        ProjectId: 'production-karte-io-systems',
        ProjectNumber: '994682962173',
      },
      GcpProject: {
        [GcloudProjectName.Base]: {
          ProjectId: 'production-rt-systems',
          ProjectNumber: '402791595004',
        },
        [GcloudProjectName.Knowledge]: {
          ProjectId: 'production-rt-knowledge',
          ProjectNumber: '473576939563',
        },
        [GcloudProjectName.Speak]: {
          ProjectId: 'production-rt-speak',
          ProjectNumber: '811751347331',
        },
        [GcloudProjectName.Debeso]: {
          ProjectId: 'production-rt-debeso',
          ProjectNumber: '475156578770',
        },
        [GcloudProjectName.Debrief]: {
          ProjectId: 'production-rt-debrief',
          ProjectNumber: '247370717440',
        },
        [GcloudProjectName.Troppus]: {
          ProjectId: 'production-rt-troppus',
          ProjectNumber: '350934821882',
        },
        [GcloudProjectName.Tabula]: {
          ProjectId: 'production-rt-tabula',
          ProjectNumber: '1057735185552',
        },
        [GcloudProjectName.Intelligence]: {
          ProjectId: 'production-rt-intelligence',
          ProjectNumber: '923137037735',
        },
        [GcloudProjectName.Voc]: {
          ProjectId: 'production-rt-voc',
          ProjectNumber: '890926213256',
        },
        [GcloudProjectName.QantDataHub]: {
          ProjectId: 'prod-qant-data-hub',
          ProjectNumber: '150954085667',
        },
        [GcloudProjectName.Boilerplate]: {
          ProjectId: 'production-rt-boilerplate',
          ProjectNumber: '979542900565',
        },
        [GcloudProjectName.Neuro]: {
          ProjectId: 'production-rt-neuro',
          ProjectNumber: '763193376007',
        },
        [GcloudProjectName.Coach]: {
          ProjectId: 'production-rt-coach',
          ProjectNumber: '335651521692',
        },
        [GcloudProjectName.PubApi]: {
          ProjectId: 'production-rt-pubapi',
          ProjectNumber: '1004795013609',
        },
        [GcloudProjectName.SmartFaq]: {
          ProjectId: 'production-rt-smart-faq',
          ProjectNumber: '13474264173',
        },
        // prod の CDE プロジェクトは未作成。作成後に ProjectNumber を埋める (docs/pcidss/adr/0004)。
        [GcloudProjectName.Cde]: {
          ProjectId: 'production-rt-cde',
          ProjectNumber: '',
        },
        Pii: {
          [GcloudProjectName.Base]: {
            ProjectId: 'production-rt-systems-pii',
            ProjectNumber: '768973697261',
          },
          [GcloudProjectName.Troppus]: {
            ProjectId: 'production-rt-troppus-pii',
            ProjectNumber: '709043399057',
          },
          [GcloudProjectName.Neuro]: {
            ProjectId: 'production-rt-neuro-pii',
            ProjectNumber: '139508671660',
          },
          [GcloudProjectName.Speak]: {
            ProjectId: 'production-rt-speak-pii',
            ProjectNumber: '326719800951',
          },
        },
      },
    },
  } satisfies {
    [K in Env]: {
      ProjectId: string;
      ProjectNumber?: string;
      Pii?: { ProjectId: string; ProjectNumber: string };
      PlaidSystems?: { ProjectId: string; ProjectNumber: string };
      GcpProject?: {
        [K in GcloudProjectName]: { ProjectId: string; ProjectNumber: string };
      } & {
        Pii: Partial<{
          [K in GcloudProjectName]: {
            ProjectId: string;
            ProjectNumber: string;
          };
        }>;
      };
    };
  };
}
